import React, { useEffect, useRef, useState } from 'react';
import { fetchShopAvailabilitySlots } from '../utils/partnerShopApi';

// A shop's availability slots come back in the shop's OWN timezone, e.g.
// "2026-09-10T09:00:00-06:00". The wall-clock date and time we book with are simply
// the leading characters of that string — reading them straight from it keeps the
// instant we book identical to the slot the driver was offered (no re-parsing drift).
const slotDate = (iso) => String(iso || '').slice(0, 10);
const slotTime = (iso) => String(iso || '').slice(11, 16);

const to12h = (hhmm) => {
    const [h, m] = String(hhmm).split(':').map(Number);
    if (Number.isNaN(h)) return hhmm;
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
};

const formatDisplayDate = (yyyyMmDd) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(yyyyMmDd || ''));
    if (!match) return '';
    return `${match[2]}/${match[3]}/${match[1]}`;
};

// The current wall-clock date + time in the SHOP's timezone (this is a US product, so a
// drop-off's "now" is the shop's US local time, not the driver's). Derived from the real
// client clock so it's correct even if the server clock drifts. Used to hide same-day slots
// that have already passed.
const shopNow = (tz) => {
    try {
        const f = new Intl.DateTimeFormat('en-US', {
            timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
        });
        const p = Object.fromEntries(f.formatToParts(new Date()).map((x) => [x.type, x.value]));
        return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
    } catch {
        return { date: '', time: '' };
    }
};

// Date picker + a live grid of the shop's genuinely-open drop-off times for that date.
// Full and past slots never appear here (the backend subtracts existing bookings and the
// shop's notice window); the definitive no-double-booking guarantee is still enforced when
// the booking row is written, so `reloadToken` lets the parent force a refresh after a
// race is lost ("that slot was just taken — pick another").
export default function AppointmentSlotPicker({
    shopId,
    minDate,
    selectedDate,
    onSelectDate,
    selectedTime,
    onSelectSlot,
    reloadToken = 0,
    disabled = false,
}) {
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [notConfigured, setNotConfigured] = useState(false);
    const [closureNote, setClosureNote] = useState('');
    const [blockedWindows, setBlockedWindows] = useState([]);
    const dateInputRef = useRef(null);

    // Clicking a transparent date input only FOCUSES it in Chrome — the native picker
    // doesn't reliably open (the "sometimes nothing happens" lag). showPicker() opens it
    // deterministically on the click gesture; wrapped because older browsers lack it.
    const openDatePicker = () => {
        const el = dateInputRef.current;
        if (!el || disabled) return;
        try { el.showPicker?.(); } catch { /* fall back to default focus behavior */ }
    };

    useEffect(() => {
        let cancelled = false;
        if (!shopId || !selectedDate) {
            setSlots([]);
            setClosureNote('');
            setBlockedWindows([]);
            return undefined;
        }
        setLoading(true);
        setLoadError('');
        fetchShopAvailabilitySlots(shopId, selectedDate, selectedDate)
            .then((data) => {
                if (cancelled) return;
                setNotConfigured(Boolean(data) && data.configured === false);
                // Hide same-day slots that have already passed in the shop's US local time,
                // so a driver can never pick a drop-off in the past (e.g. no 9 AM once it's
                // 3 PM at the shop). Future dates keep every slot.
                const now = shopNow(data?.timezone || '');
                const isToday = selectedDate === now.date;
                const daySlots = (data?.slots || [])
                    .filter((s) => s.available && slotDate(s.slot_start) === selectedDate)
                    .filter((s) => !isToday || !now.time || slotTime(s.slot_start) >= now.time)
                    .map((s) => ({ time: slotTime(s.slot_start), remaining: s.remaining, label: to12h(slotTime(s.slot_start)) }));
                setSlots(daySlots);
                const dayClosures = (data?.closures || []).filter((c) => c.on_date === selectedDate);
                const full = dayClosures.find((c) => c.is_full_day);
                if (full) {
                    // A full-day closure blocks the whole day: show the reason instead of a grid.
                    setClosureNote(full.name ? `Closed — ${full.name}` : 'The shop is closed this day.');
                    setBlockedWindows([]);
                } else {
                    // Partial blocks only carve out their own window — the backend already dropped
                    // those slots, so the rest of the day stays bookable. Surface them as an info
                    // banner ABOVE the grid, never in place of it.
                    setClosureNote('');
                    setBlockedWindows(dayClosures.map((c) => (
                        `${to12h(c.start_time)}–${to12h(c.end_time)}${c.name ? ` (${c.name})` : ''}`
                    )));
                }
            })
            .catch((err) => {
                if (cancelled) return;
                setLoadError(err?.message || 'Could not load available times.');
                setSlots([]);
                setClosureNote('');
                setBlockedWindows([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [shopId, selectedDate, reloadToken]);

    return (
        <div>
            <div className="block">
                <span className="mb-1.5 block text-[10px] font-black uppercase tracking-[0.08em] text-[#414141]">Drop-off date</span>
                <div className="relative h-12">
                    <input
                        ref={dateInputRef}
                        type="date"
                        lang="en-US"
                        value={selectedDate || ''}
                        min={minDate || ''}
                        disabled={disabled}
                        onChange={(event) => onSelectDate?.(event.target.value)}
                        onClick={openDatePicker}
                        onFocus={openDatePicker}
                        className="peer absolute inset-0 z-10 h-12 w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                        aria-label="Choose drop-off date"
                    />
                    <div className="flex h-12 w-full items-center justify-between rounded-xl border border-[#d9d9d9] bg-white px-3 text-left text-sm font-black text-[#414141] outline-none transition peer-hover:border-[#B3B3B3] peer-focus:border-[#D81F26] peer-focus:ring-2 peer-focus:ring-[#D81F26]/15 peer-disabled:opacity-60">
                        <span>{formatDisplayDate(selectedDate) || 'MM/DD/YYYY'}</span>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <rect x="3" y="4" width="18" height="18" rx="2" />
                            <path d="M16 2v4M8 2v4M3 10h18" />
                        </svg>
                    </div>
                </div>
            </div>

            <div className="mt-3">
                <span className="mb-1.5 block text-[10px] font-black uppercase tracking-[0.08em] text-[#414141]">Available drop-off times</span>
                {/* Partial blocks are informational only — the blocked slots are already gone
                    from the grid below, the rest of the day stays bookable. Only shown when
                    there ARE free slots, so it never contradicts a "No open times" day. */}
                {!loading && !closureNote && blockedWindows.length > 0 && slots.length > 0 && (
                    <p className="mb-2 rounded-xl border border-[#f6dcc0] bg-[#fdf4e8] px-3 py-2 text-xs font-bold text-[#9a5407]">
                        Shop unavailable {blockedWindows.join(', ')} — other times are open.
                    </p>
                )}
                {loading ? (
                    <p className="rounded-xl bg-white/70 px-3 py-3 text-sm font-semibold text-[#414141]">Loading times…</p>
                ) : loadError ? (
                    <p className="rounded-xl border border-[#ffd2d2] bg-[#fff1f1] px-3 py-2 text-sm font-bold text-[#b42318]">{loadError}</p>
                ) : notConfigured ? (
                    <p className="rounded-xl bg-white/70 px-3 py-3 text-sm font-semibold text-[#414141]">This shop hasn’t published booking times yet.</p>
                ) : closureNote ? (
                    <div className="rounded-xl border border-[#f4c2c2] bg-[#fdf1f1] px-3 py-3">
                        <p className="text-sm font-black text-[#b42318]">{closureNote}</p>
                        <p className="mt-0.5 text-xs font-semibold text-[#b46a6a]">Please choose another date.</p>
                    </div>
                ) : slots.length === 0 ? (
                    <p className="rounded-xl bg-white/70 px-3 py-3 text-sm font-semibold text-[#414141]">No open times on this day. Try another date.</p>
                ) : (
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                        {slots.map((s) => {
                            const active = s.time === selectedTime;
                            return (
                                <button
                                    key={s.time}
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => onSelectSlot?.(selectedDate, s.time)}
                                    title={s.remaining > 1 ? `${s.remaining} seats left` : undefined}
                                    className={`min-h-[38px] rounded-xl border px-2 text-xs font-black transition disabled:opacity-60 ${active
                                        ? 'border-[#414141] bg-[#414141] text-white'
                                        : 'border-[#d9d9d9] bg-white text-[#414141] hover:border-[#414141] hover:bg-[#F9F9F9]'}`}
                                >
                                    {s.label}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
